import { h } from '@omni-torn/ui';
import { PLAN_ASSUMPTIONS, planText, SELL_FEE, stockPlans, topPlans, yearlyPerBlock } from '@omni-torn/shared/stock-plan';
import { blockReturn, investmentRows, portfolio, readHoldings, readStocks, stockSignal, stockStats, type Holding, type InvestRow, type Line, type Stock, type StockHistoryAnswer, type StockMarketRow, type StocksAnswer, type StockSignal, type StockStats } from '@omni-torn/shared/stocks';
import type { BankRate, TornRefAnswer } from '@omni-torn/shared/torn-ref';
import { BANK_MAX } from '@omni-torn/shared/torn-sims';
import { setAddressQuery } from '../log-kit/ui';
import { server, torn } from '../session';
import { ago, box, facts, kids, lineChart, loading, money, num, problem, short, signed, table, tabs } from '../ui';
import { ref } from './torn';

/**
 * Stocks (website roadmap S13: Torn Intel's stock tracker, FF Scouter's investment ROI calculator, Torn Intel's Trader
 * view). Four tabs:
 *  - Your shares: what you hold (your key, in this browser), its worth, profit and loss, benefit blocks, the next block and
 *    the stock planner (the extension's, packages/shared stock-plan.ts).
 *  - Market: every stock's price now (your key), changes over a day / week / month / year, the 30-day range and a
 *    sparkline, from the prices the Omni-Torn server keeps (`GET /api/stocks`: Torn's public list read with the server's
 *    own key every 5–60 minutes — no player data); one stock's chart (`/api/stocks/:id/history`).
 *  - Investments: every benefit block, the bank's terms and an optional private island rental by yearly return, with a
 *    budget filter (packages/shared stocks.ts `investmentRows`; bank rates and item values from the server's Torn lists).
 *  - Trader: simple rules of thumb from past prices (near the monthly low and turning up…) — plainly not advice.
 * Torn is asked twice with your key (the market and your shares, kept 2 minutes); everything else comes from the server.
 */

type Tab = 'yours' | 'market' | 'invest' | 'trader';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'yours', label: 'Your shares' },
  { id: 'market', label: 'Market' },
  { id: 'invest', label: 'Investments' },
  { id: 'trader', label: 'Trader' },
];
const OPTS_KEY = 'omni-torn.stocksInvest';

interface InvestOpts {
  budget: number;
  merits: number;
  tci: boolean;
  topBank: boolean;
  hideOwned: boolean;
  /** Every block up to the 10th (else the ones you own and the next three). */
  allBlocks: boolean;
  piCost: number;
  piRent: number;
  piUpkeep: number;
}
const DEFAULT_OPTS: InvestOpts = { budget: 0, merits: 0, tci: false, topBank: true, hideOwned: false, allBlocks: false, piCost: 0, piRent: 0, piUpkeep: 0 };

function loadOpts(): InvestOpts {
  try {
    const o = JSON.parse(localStorage.getItem(OPTS_KEY) ?? '{}') as Partial<InvestOpts>;
    return { ...DEFAULT_OPTS, ...Object.fromEntries(Object.entries(o).filter(([k, v]) => k in DEFAULT_OPTS && typeof v === typeof DEFAULT_OPTS[k as keyof InvestOpts])) };
  } catch {
    return { ...DEFAULT_OPTS };
  }
}
function saveOpts(o: InvestOpts): void {
  try {
    localStorage.setItem(OPTS_KEY, JSON.stringify(o));
  } catch {
    /* this visit only */
  }
}

interface Data {
  stocks: Stock[];
  holdings: Holding[];
  /** The server's history figures by stock id (empty when it has none). */
  hist: Map<number, StockMarketRow>;
  answer: StocksAnswer | null;
  /** Prices are the viewer's live read (else the server's last one). */
  live: boolean;
  itemValue: (name: string) => number | null;
  bank: BankRate[];
}

export async function renderStocks(view: HTMLElement): Promise<void> {
  const q = new URLSearchParams(location.search);
  let tab: Tab = TABS.find((t) => t.id === q.get('tab'))?.id ?? 'yours';
  const body = h('div', {}, loading('Reading the stock market…'));
  const status = h('p', { class: 'muted small', 'data-stocks-status': '' });
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Stocks'), h('p', { class: 'muted' }, 'Your shares, every stock’s price over time, what each investment returns a year, and simple rules of thumb.')),
    tabs(TABS, tab, (id) => {
      tab = id as Tab;
      setAddressQuery(null, { tab: tab === 'yours' ? null : tab, stock: tab === 'market' ? new URLSearchParams(location.search).get('stock') : null });
      draw();
    }),
    status,
    body,
  );

  let data: Data;
  try {
    data = await load();
  } catch (err) {
    body.replaceChildren(problem(err));
    return;
  }
  const a = data.answer;
  status.replaceChildren(
    data.live ? 'Prices: Torn, just now (your key). ' : 'Prices: the Omni-Torn server’s last reading (your key couldn’t read Torn’s market). ',
    a?.at ? `History: kept by the Omni-Torn server, read from Torn every ${a.every} minutes — last ${ago(a.at)}${oldest(data) ? `, since ${new Date(oldest(data)!).toLocaleDateString()}` : ''}.` : a?.polling ? 'History: the server starts keeping prices with its first reading.' : 'History: this server doesn’t keep stock prices.',
  );

  function draw(): void {
    body.replaceChildren(tab === 'market' ? marketTab(data) : tab === 'invest' ? investTab(data) : tab === 'trader' ? traderTab(data) : yoursTab(data));
  }
  draw();
}

async function load(): Promise<Data> {
  const [market, mine, answer, items, bank] = await Promise.all([
    torn('/torn/', { selections: 'stocks' }, 120_000).catch((e: Error) => e),
    torn('/user/', { selections: 'stocks' }, 120_000),
    server<StocksAnswer>('GET', '/api/stocks').catch(() => null),
    ref('items').catch(() => null),
    ref('bank').catch(() => null),
  ]);
  const liveStocks = market instanceof Error ? [] : readStocks(market);
  const stocks = liveStocks.length ? liveStocks : (answer?.stocks ?? []).map(({ stats: _, ...s }) => s);
  if (!stocks.length) throw market instanceof Error ? market : new Error('Torn sent no stocks.');
  const values = new Map<string, number>();
  for (const it of (items as TornRefAnswer<'items'> | null)?.data ?? []) if (it.marketValue) values.set(it.name.toLowerCase(), it.marketValue);
  return {
    stocks,
    holdings: readHoldings(mine),
    hist: new Map((answer?.stocks ?? []).map((s) => [s.id, s])),
    answer,
    live: liveStocks.length > 0,
    itemValue: (name) => values.get(name.toLowerCase()) ?? null,
    bank: (bank as TornRefAnswer<'bank'> | null)?.data ?? [],
  };
}

const oldest = (d: Data): number | null => {
  const xs = [...d.hist.values()].map((s) => s.stats.since).filter((x): x is number => x !== null);
  return xs.length ? Math.min(...xs) : null;
};

/** Figures for a stock at today's price (the server's history with the live price on top). */
function statsOf(d: Data, s: Stock): StockStats | null {
  const row = d.hist.get(s.id);
  if (!row) return null;
  if (row.price === s.price) return row.stats;
  // The live price moved since the server's reading: the range and sparkline take it in.
  const st = row.stats;
  return { ...st, low30: st.low30 === null ? null : Math.min(st.low30, s.price), high30: st.high30 === null ? null : Math.max(st.high30, s.price), spark: st.spark.length ? [...st.spark.slice(0, -1), s.price] : st.spark };
}

const stockCell = (s: Stock) => h('span', {}, h('strong', {}, s.acronym), h('span', { class: 'muted small' }, ` ${s.name}`));
const price = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortMoney = (n: number) => `$${short(n)}`;
const pctCell = (n: number | null) => (n === null ? h('span', { class: 'muted' }, '—') : h('span', { class: n > 0 ? 't-green' : n < 0 ? 't-red' : '' }, `${n > 0 ? '+' : n < 0 ? '−' : ''}${num(Math.abs(n), 2)}%`));
const days = (n: number | null) => (n === null ? '—' : n >= 730 ? `${num(n / 365, 1)} years` : `${num(n)} days`);
const benefitText = (s: Stock) => (s.benefit ? `${s.benefit}${s.kind === 'passive' ? ' (passive)' : s.frequency ? ` every ${s.frequency} days` : ''}` : '—');

/** A tiny line of prices (no axes). */
function sparkline(values: readonly number[], label: string): HTMLElement | SVGElement {
  if (values.length < 2) return h('span', { class: 'muted small' }, '—');
  const W = 90;
  const H = 22;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const y = (v: number) => (hi === lo ? H / 2 : 2 + (1 - (v - lo) / (hi - lo)) * (H - 4));
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${((i / (values.length - 1)) * W).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
  svg.setAttribute('class', `spark ${values.at(-1)! >= values[0]! ? 'spark-up' : 'spark-down'}`);
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', label);
  const path = document.createElementNS(ns, 'path');
  path.setAttribute('d', d);
  svg.append(path);
  return svg;
}

// ---------------------------------------------------------------- your shares

function yoursTab(d: Data): HTMLElement {
  const p = portfolio(d.stocks, d.holdings);
  const held = p.lines.filter((l) => l.shares > 0).sort((a, b) => b.value - a.value);
  const byId = new Map(d.holdings.map((x) => [x.id, x]));
  const yearlyAll = held.reduce((t, l) => t + (yearlyPerBlock(l, d.itemValue) ?? 0) * l.blocks, 0);
  const plans = topPlans(stockPlans({ lines: p.lines, itemValue: d.itemValue }), 3);
  return h(
    'div',
    {},
    h(
      'div',
      { class: 'tiles' },
      tile('Your shares are worth', money(p.value)),
      tile('Bought for', p.paid !== null ? money(p.paid) : '—'),
      tile('Profit', p.gain !== null ? signed(p.gain, (n) => money(n)) : '—', p.gain !== null && p.paid ? `${signed(Math.round((p.gain / p.paid) * 1000) / 10, (n) => num(n, 1))}%` : undefined),
      tile('Benefits ready', String(held.filter((l) => byId.get(l.stock.id)?.ready).length)),
      tile('Money benefits a year', yearlyAll ? money(yearlyAll) : '—'),
    ),
    box(
      'Your shares',
      table(
        held,
        [
          { label: 'Stock', get: (l) => stockCell(l.stock), sort: (l) => l.stock.acronym },
          { label: 'Shares', get: (l) => num(l.shares), sort: (l) => l.shares, cls: 'r' },
          { label: 'Price', get: (l) => price(l.stock.price), sort: (l) => l.stock.price, cls: 'r' },
          { label: 'Worth', get: (l) => money(l.value), sort: (l) => l.value, cls: 'r' },
          { label: 'Bought at', get: (l) => (l.paid !== null ? price(l.paid / l.shares) : '—'), sort: (l) => (l.paid !== null ? l.paid / l.shares : 0), cls: 'r' },
          { label: 'Profit', get: (l) => (l.gain !== null ? h('span', { class: l.gain >= 0 ? 't-green' : 't-red' }, signed(l.gain, shortMoney)) : '—'), sort: (l) => l.gain ?? 0, cls: 'r' },
          { label: 'Profit %', get: (l) => pctCell(l.gain !== null && l.paid ? Math.round((l.gain / l.paid) * 10_000) / 100 : null), sort: (l) => (l.gain !== null && l.paid ? l.gain / l.paid : 0), cls: 'r' },
          { label: 'If sold now', get: (l) => money(Math.floor(l.value * (1 - SELL_FEE))), sort: (l) => l.value, cls: 'r' },
          { label: 'Blocks', get: (l) => blocksCell(l, byId.get(l.stock.id)), sort: (l) => l.blocks },
          { label: 'Next block', get: (l) => (l.nextShares && l.nextCost ? h('span', {}, money(l.nextCost), h('span', { class: 'muted small' }, ` ${short(l.nextShares)} shares`)) : '—'), sort: (l) => l.nextCost ?? 0, cls: 'r' },
        ],
        { initial: 3, empty: 'You don’t own any shares.', remember: 'stocks-yours' },
      ),
      { id: 'stocks-yours' },
    ),
    box(
      'Planner',
      plans.length
        ? [h('ul', { class: 'plain-list', 'data-stock-plans': '' }, ...plans.map((x) => h('li', {}, planText(x)))), h('p', { class: 'muted small', title: PLAN_ASSUMPTIONS }, 'Worked out from today’s prices and each benefit’s money a year (hover for how). A sum, not advice: prices move.')]
        : h('p', { class: 'muted' }, held.length ? 'Nothing to swap: no sale buys blocks that pay more a year than what you have.' : 'Buy a benefit block and the planner shows what to sell or save for next.'),
      { id: 'stocks-planner' },
    ),
  );
}

function blocksCell(l: Line, hold: Holding | undefined): HTMLElement | string {
  if (!l.stock.requirement) return '—';
  const ready = hold?.ready ? h('span', { class: 't-green' }, ' ready') : hold?.progress !== null && hold?.progress !== undefined && l.stock.frequency && l.blocks ? h('span', { class: 'muted small' }, ` ${hold.progress}/${l.stock.frequency} days`) : null;
  return h('span', {}, l.stock.kind === 'passive' ? (l.blocks ? 'on' : 'off') : String(l.blocks), ready);
}

function tile(label: string, value: string, change?: string): HTMLElement {
  return h('div', { class: 'tile' }, h('div', { class: 'tile-label' }, label), h('div', { class: 'tile-value' }, value), change ? h('div', { class: 'tile-change' }, change) : null);
}

// ---------------------------------------------------------------- market

function marketTab(d: Data): HTMLElement {
  const detail = h('div', { 'data-stock-detail': '' });
  const pick = (s: Stock) => {
    setAddressQuery(null, { stock: s.acronym });
    void showStock(detail, d, s, 30);
  };
  const rows = d.stocks.map((s) => ({ s, st: statsOf(d, s) }));
  const wrap = h(
    'div',
    {},
    box(
      'Market',
      [
        table(
          rows,
          [
            { label: 'Stock', get: (r) => h('button', { type: 'button', class: 'abroad-pick', 'data-stock': r.s.acronym, onclick: () => pick(r.s) }, stockCell(r.s)), sort: (r) => r.s.acronym },
            { label: 'Price', get: (r) => price(r.s.price), sort: (r) => r.s.price, cls: 'r' },
            { label: 'Day', get: (r) => pctCell(r.st?.change.d1 ?? null), sort: (r) => r.st?.change.d1 ?? -1e9, cls: 'r' },
            { label: 'Week', get: (r) => pctCell(r.st?.change.d7 ?? null), sort: (r) => r.st?.change.d7 ?? -1e9, cls: 'r' },
            { label: '30 days', get: (r) => pctCell(r.st?.change.d30 ?? null), sort: (r) => r.st?.change.d30 ?? -1e9, cls: 'r' },
            { label: 'Year', get: (r) => pctCell(r.st?.change.d365 ?? null), sort: (r) => r.st?.change.d365 ?? -1e9, cls: 'r' },
            { label: '30-day range', get: (r) => (r.st?.low30 !== null && r.st?.low30 !== undefined ? `${price(r.st.low30)} – ${price(r.st.high30!)}` : '—'), cls: 'r' },
            { label: '30 days', get: (r) => sparkline(r.st?.spark ?? [], `${r.s.acronym}, the last 30 days`) },
            { label: 'Market cap', get: (r) => (r.s.marketCap ? short(r.s.marketCap) : '—'), sort: (r) => r.s.marketCap ?? 0, cls: 'r' },
            { label: 'Investors', get: (r) => (r.s.investors ? num(r.s.investors) : '—'), sort: (r) => r.s.investors ?? 0, cls: 'r' },
            { label: 'Benefit', get: (r) => h('span', { class: 'small' }, benefitText(r.s), r.s.requirement ? h('span', { class: 'muted' }, ` · ${short(r.s.requirement)} shares`) : null) },
          ],
          { initial: 0, desc: false, remember: 'stocks-market' },
        ),
        h('p', { class: 'muted small' }, 'Changes and ranges come from the prices the Omni-Torn server keeps; “—” until its history reaches back that far. Click a stock for its chart.'),
      ],
      { id: 'stocks-market' },
    ),
    detail,
  );
  const want = new URLSearchParams(location.search).get('stock');
  const first = d.stocks.find((s) => s.acronym === want?.toUpperCase());
  if (first) void showStock(detail, d, first, 30);
  return wrap;
}

async function showStock(holder: HTMLElement, d: Data, s: Stock, span: number): Promise<void> {
  holder.replaceChildren(box(`${s.acronym} · ${s.name}`, loading('Reading its prices…'), { id: 'stock-chart' }));
  let points: StockHistoryAnswer['points'] = [];
  let note: string | null = null;
  try {
    points = (await server<StockHistoryAnswer>('GET', `/api/stocks/${s.id}/history?days=${span}`)).points;
  } catch (err) {
    note = (err as Error).message;
  }
  const now = Date.now();
  const chartPoints = [...points.map((p) => ({ at: p.at, v: p.price })), { at: now, v: s.price }];
  const spread = Math.max(...chartPoints.map((p) => p.v)) - Math.min(...chartPoints.map((p) => p.v));
  const st = statsOf(d, s) ?? stockStats(points, s.price, now);
  // One block held over the history kept (up to a year): from the oldest price the server has within the year.
  const yearRet = st.yearStart ? blockReturn(s, [st.yearStart], now, d.itemValue) : null;
  const spans = [
    { id: '1', label: 'Day' },
    { id: '7', label: 'Week' },
    { id: '30', label: '30 days' },
    { id: '365', label: 'Year' },
  ];
  holder.replaceChildren(
    box(
      `${s.acronym} · ${s.name}`,
      kids(
        tabs(spans, String(span), (id) => void showStock(holder, d, s, Number(id))),
        note ? h('p', { class: 'muted' }, note) : null,
        lineChart(chartPoints, { label: `${s.acronym} price`, format: (n) => `$${num(n, spread < 20 ? 2 : 0)}`, ...(span <= 1 ? { xFormat: (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) } : {}) }),
        facts([
          ['Price now', price(s.price)],
          ['30-day range', st.low30 !== null ? `${price(st.low30)} – ${price(st.high30!)}` : '—'],
          ['Change: day / week / 30 days / year', h('span', {}, pctCell(st.change.d1), ' / ', pctCell(st.change.d7), ' / ', pctCell(st.change.d30), ' / ', pctCell(st.change.d365))],
          ['Benefit', `${benefitText(s)}${s.requirement ? ` · first block ${short(s.requirement)} shares (${money(s.requirement * s.price)})` : ''}`],
          [
            'One block held',
            yearRet && yearRet.days !== null
              ? h('span', { 'data-block-return': '' }, `over the last ${yearRet.days} days: price `, pctCell(yearRet.price), yearRet.withBenefit !== null ? h('span', {}, ', with its benefit ', pctCell(yearRet.withBenefit)) : null)
              : h('span', { class: 'muted' }, 'needs a week of prices'),
          ],
        ]),
      ),
      { id: 'stock-chart' },
    ),
  );
}

// ---------------------------------------------------------------- investments (ROI)

function investTab(d: Data): HTMLElement {
  const o = loadOpts();
  const out = h('div', {});
  const field = (label: string, key: 'budget' | 'piCost' | 'piRent' | 'piUpkeep', hint: string) => {
    const input = h('input', { type: 'text', inputmode: 'numeric', value: o[key] ? num(o[key]) : '', placeholder: hint, 'data-invest': key }) as HTMLInputElement;
    input.addEventListener('change', () => {
      o[key] = Math.max(0, Number(input.value.replace(/[^\d.]/g, '')) || 0);
      input.value = o[key] ? num(o[key]) : '';
      saveOpts(o);
      draw();
    });
    return h('label', { class: 'calc-field' }, label, input);
  };
  const check = (label: string, key: 'tci' | 'topBank' | 'hideOwned' | 'allBlocks') => {
    const c = h('input', { type: 'checkbox', 'data-invest': key, ...(o[key] ? { checked: true } : {}) }) as HTMLInputElement;
    c.addEventListener('change', () => {
      o[key] = c.checked;
      saveOpts(o);
      draw();
    });
    return h('label', { class: 'check' }, c, ` ${label}`);
  };
  const merits = h('select', { 'aria-label': 'Bank Interest merits', 'data-invest': 'merits' }, ...Array.from({ length: 11 }, (_, i) => h('option', { value: String(i), ...(i === o.merits ? { selected: true } : {}) }, `${i} merits (+${i * 5}%)`))) as HTMLSelectElement;
  merits.addEventListener('change', () => {
    o.merits = Number(merits.value);
    saveOpts(o);
    draw();
  });

  function draw(): void {
    const { rows, noValue } = investmentRows({
      stocks: d.stocks,
      holdings: d.holdings,
      itemValue: d.itemValue,
      bank: d.bank,
      bankOpts: { merits: o.merits, tci: o.tci, amount: o.budget ? Math.min(o.budget, BANK_MAX) : BANK_MAX },
      property: o.piCost && o.piRent ? { cost: o.piCost, rentPerDay: o.piRent, upkeepPerDay: o.piUpkeep } : null,
    });
    const bestBank = rows.filter((r) => r.kind === 'bank').sort((a, b) => b.roi - a.roi)[0]?.key;
    const next = new Map(rows.filter((r) => r.next).map((r) => [r.stockId, r.block!]));
    const nearEnough = (r: InvestRow) => o.allBlocks || r.kind !== 'block' || r.owned || r.block! < (next.get(r.stockId) ?? 1) + 3;
    const shown = rows.filter((r) => (!o.budget || r.owned || r.cost <= o.budget) && !(o.hideOwned && r.owned) && !(o.topBank && r.kind === 'bank' && r.key !== bestBank) && nearEnough(r));
    out.replaceChildren(
      ...kids(
        table(
          shown,
          [
            { label: 'Investment', get: (r) => investLabel(r), sort: (r) => r.label },
            { label: 'Costs', get: (r) => money(r.cost), sort: (r) => r.cost, cls: 'r' },
            { label: 'Pays a year', get: (r) => money(r.yearly), sort: (r) => r.yearly, cls: 'r' },
            { label: 'Return a year', get: (r) => `${num(r.roi, 2)}%`, sort: (r) => r.roi, cls: 'r' },
            { label: 'Pays for itself in', get: (r) => (r.kind === 'bank' ? h('span', { class: 'muted small' }, `money back after ${r.days} days`) : days(r.payback)), sort: (r) => r.payback ?? 0, cls: 'r' },
            { label: 'What it is', get: (r) => h('span', { class: 'small' }, investWhat(d, r)) },
          ],
          { initial: 3, empty: o.budget ? `Nothing costs ${money(o.budget)} or less.` : 'Nothing to compare yet.', remember: 'stocks-invest' },
        ),
        noValue.length ? h('p', { class: 'muted small', 'data-no-value': '' }, `Not ranked (their benefit has no money value): ${noValue.map((s) => `${s.acronym} — ${s.benefit}`).join('; ')}.`) : null,
      ),
    );
  }
  draw();
  return box(
    'Investments by yearly return',
    [
      h('div', { class: 'calc-fields' }, field('Budget', 'budget', 'any'), h('label', { class: 'calc-field' }, 'Bank merits', merits), field('Private island: cost', 'piCost', 'optional'), field('Rent a day', 'piRent', 'optional'), field('Upkeep a day', 'piUpkeep', '0')),
      h('div', { class: 'calc-row' }, check('Torn City Investments perk (+10% bank)', 'tci'), check('Top bank term only', 'topBank'), check('Hide blocks you own', 'hideOwned'), check('Every block (to the 10th)', 'allBlocks')),
      out,
      h(
        'p',
        { class: 'muted small' },
        'Each benefit block pays its benefit once per period (money as it says, items at today’s market value); block n needs 2^(n−1) × the first block’s shares. The bank takes at most $2b at a time and gives your money back at the end of the term. Blocks you own are in bold, the next one to buy is marked; the three after it show unless “Every block” is ticked. Prices move — this is arithmetic, not advice.',
      ),
    ],
    { id: 'stocks-invest' },
  );
}

function investLabel(r: InvestRow): HTMLElement {
  return h('span', { class: r.owned ? 'is-mine' : '', 'data-invest-row': r.key }, r.label, r.owned ? h('span', { class: 'badge on' }, 'owned') : null, r.next ? h('span', { class: 'badge' }, 'next') : null);
}

function investWhat(d: Data, r: InvestRow): string {
  if (r.kind === 'bank') return `Torn’s bank, ${r.days} days`;
  if (r.kind === 'property') return 'Rented out: rent minus upkeep';
  const s = d.stocks.find((x) => x.id === r.stockId);
  return s ? `${benefitText(s)} · ${short(r.shares)} shares` : '';
}

// ---------------------------------------------------------------- trader (rules of thumb)

const SIGNAL_TEXT: Record<StockSignal['kind'], string> = { buy: 'Looks cheap', sell: 'Looks dear', hold: 'Wait', unknown: '—' };

function traderTab(d: Data): HTMLElement {
  const now = Date.now();
  const p = portfolio(d.stocks, d.holdings);
  const lineOf = new Map(p.lines.map((l) => [l.stock.id, l]));
  const rows = d.stocks.map((s) => {
    const st = statsOf(d, s);
    return { s, st, sig: st ? stockSignal(st, s.price, now) : stockSignal(stockStats([], s.price, now), s.price, now), line: lineOf.get(s.id)! };
  });
  return h(
    'div',
    {},
    h('div', { class: 'notice', 'data-rules-of-thumb': '' }, h('strong', {}, 'Rules of thumb, not advice. '), 'These look only at past prices: “looks cheap” = near its 30-day low and not still falling; “looks dear” = near its 30-day high and not still rising (today’s price against the last day’s average). They know nothing about what comes next, Torn’s news or what a benefit is worth to you.'),
    box(
      'Trader',
      table(
        rows,
        [
          { label: 'Stock', get: (r) => stockCell(r.s), sort: (r) => r.s.acronym },
          { label: 'Price', get: (r) => price(r.s.price), sort: (r) => r.s.price, cls: 'r' },
          { label: 'Rule of thumb', get: (r) => h('span', { class: `badge signal-${r.sig.kind}`, 'data-signal': r.sig.kind }, SIGNAL_TEXT[r.sig.kind]), sort: (r) => ({ buy: 0, hold: 1, sell: 2, unknown: 3 })[r.sig.kind] },
          { label: 'Why', get: (r) => h('span', { class: 'small' }, r.sig.why) },
          { label: 'In its 30-day range', get: (r) => (r.sig.place === null ? '—' : `${r.sig.place}%`), sort: (r) => r.sig.place ?? -1, cls: 'r' },
          { label: '30 days', get: (r) => sparkline(r.st?.spark ?? [], `${r.s.acronym}, the last 30 days`) },
          { label: 'You hold', get: (r) => (r.line.shares ? short(r.line.shares) : '—'), sort: (r) => r.line.shares, cls: 'r' },
          { label: 'Your profit', get: (r) => (r.line.gain !== null ? h('span', { class: r.line.gain >= 0 ? 't-green' : 't-red' }, signed(r.line.gain, shortMoney)) : '—'), sort: (r) => r.line.gain ?? 0, cls: 'r' },
        ],
        { initial: 2, desc: false, remember: 'stocks-trader' },
      ),
      { id: 'stocks-trader' },
    ),
  );
}
